import {
  TASK_DUE_FILTERS,
  TASK_PRIORITIES,
  TASK_SORT_FIELDS,
  TASK_STATUSES,
  TASK_TYPES,
  TASK_VIEWS,
  type TaskListQuery,
  type TaskView,
} from '@sahab/contracts'
import { z } from 'zod'

type TaskSortField = (typeof TASK_SORT_FIELDS)[number]
export type TaskSort = TaskSortField | `-${TaskSortField}`

export const TASK_PAGE_SIZES = [25, 50, 100] as const

export function isTaskSort(value: string): value is TaskSort {
  const field = value.startsWith('-') ? value.slice(1) : value
  return (TASK_SORT_FIELDS as readonly string[]).includes(field)
}

const uuid = z.uuid().optional().catch(undefined)
/** `me`, `none` or a user id. */
const person = z
  .union([z.literal('me'), z.literal('none'), z.uuid()])
  .optional()
  .catch(undefined)
const flag = z.boolean().optional().catch(undefined)

/**
 * The register lives in the URL (shareable, back-button safe): view tab, filters, sort, page, the
 * list or board layout, and the task open in the side panel. Invalid values fall back to defaults.
 */
export const taskSearchSchema = z.object({
  layout: z.enum(['list', 'board']).optional().catch(undefined),
  view: z.enum(TASK_VIEWS).optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z
    .number()
    .refine((n): n is (typeof TASK_PAGE_SIZES)[number] => (TASK_PAGE_SIZES as readonly number[]).includes(n))
    .optional()
    .catch(undefined),
  sort: z.string().refine(isTaskSort).optional().catch(undefined),
  q: z.coerce.string().trim().max(200).optional().catch(undefined),
  status: z.enum(TASK_STATUSES).optional().catch(undefined),
  priority: z.enum(TASK_PRIORITIES).optional().catch(undefined),
  /** The task type (named apart from other lists' `type`, which means something else there). */
  taskType: z.enum(TASK_TYPES).optional().catch(undefined),
  teamId: uuid,
  assigneeId: person,
  requesterId: person,
  reviewerId: person,
  customerId: uuid,
  due: z.enum(TASK_DUE_FILTERS).optional().catch(undefined),
  dueFrom: z.iso.date().optional().catch(undefined),
  dueTo: z.iso.date().optional().catch(undefined),
  completedFrom: z.iso.date().optional().catch(undefined),
  completedTo: z.iso.date().optional().catch(undefined),
  blocked: flag,
  /** Blocked work that is mine or mine to follow up (My work's Blocked tile). */
  myBlocked: flag,
  following: flag,
  stale: flag,
  inactiveAssignee: flag,
  late: flag,
  onTime: flag,
  committedFrom: z.iso.date().optional().catch(undefined),
  committedTo: z.iso.date().optional().catch(undefined),
  deliverable: flag,
  hasCustomer: flag,
  reworked: flag,
  tag: z.coerce.string().trim().min(1).max(40).optional().catch(undefined),
  /** The task shown in the side panel. */
  task: uuid,
  /** Open the New task sheet (the command palette's "New task"). */
  new: flag,
})

export type TaskSearch = z.infer<typeof taskSearchSchema>

/** A yes/no filter as the API's query string takes it. */
function flagParam(value: boolean | undefined): 'true' | 'false' | undefined {
  return value === undefined ? undefined : value ? 'true' : 'false'
}

/** The filters the URL carries, as the API takes them (defaults applied). */
export function toTaskQuery(search: TaskSearch): TaskListQuery & { view: TaskView } {
  return {
    view: search.view ?? 'open',
    page: search.page ?? 1,
    pageSize: search.pageSize ?? TASK_PAGE_SIZES[0],
    sort: search.sort,
    q: search.q || undefined,
    status: search.status,
    priority: search.priority,
    type: search.taskType,
    teamId: search.teamId,
    assigneeId: search.assigneeId,
    requesterId: search.requesterId,
    reviewerId: search.reviewerId,
    customerId: search.customerId,
    due: search.due,
    dueFrom: search.dueFrom,
    dueTo: search.dueTo,
    completedFrom: search.completedFrom,
    completedTo: search.completedTo,
    blocked: flagParam(search.blocked),
    myBlocked: flagParam(search.myBlocked),
    following: flagParam(search.following),
    stale: flagParam(search.stale),
    inactiveAssignee: flagParam(search.inactiveAssignee),
    late: flagParam(search.late),
    onTime: flagParam(search.onTime),
    committedFrom: search.committedFrom,
    committedTo: search.committedTo,
    deliverable: flagParam(search.deliverable),
    hasCustomer: flagParam(search.hasCustomer),
    reworked: flagParam(search.reworked),
    tag: search.tag,
  }
}

/** Filters other than the view tab, search and paging: "Clear filters" resets these. */
export const FILTER_KEYS = [
  'status',
  'priority',
  'taskType',
  'teamId',
  'assigneeId',
  'requesterId',
  'reviewerId',
  'customerId',
  'due',
  'dueFrom',
  'dueTo',
  'completedFrom',
  'completedTo',
  'blocked',
  'myBlocked',
  'following',
  'stale',
  'inactiveAssignee',
  'late',
  'onTime',
  'committedFrom',
  'committedTo',
  'deliverable',
  'hasCustomer',
  'reworked',
  'tag',
] as const satisfies readonly (keyof TaskSearch)[]

export function hasFilters(search: TaskSearch): boolean {
  return FILTER_KEYS.some((key) => search[key] !== undefined)
}

/**
 * The calendar (TASKS.md §7): month or week around `date`, and the register's filters it shares (same
 * keys, so a link between the two keeps them).
 */
export const calendarSearchSchema = z.object({
  mode: z.enum(['month', 'week']).optional().catch(undefined),
  date: z.iso.date().optional().catch(undefined),
  scope: z.enum(['open', 'all']).optional().catch(undefined),
  starts: flag,
  /** Also show the service visits of my teams (read-only; with the service module and service.read). */
  visits: flag,
  q: z.coerce.string().trim().max(200).optional().catch(undefined),
  priority: z.enum(TASK_PRIORITIES).optional().catch(undefined),
  taskType: z.enum(TASK_TYPES).optional().catch(undefined),
  teamId: uuid,
  assigneeId: person,
  customerId: uuid,
  tag: z.coerce.string().trim().min(1).max(40).optional().catch(undefined),
  task: uuid,
  new: flag,
  /** The day New task was opened for. */
  newDate: z.iso.date().optional().catch(undefined),
})
export type CalendarSearch = z.infer<typeof calendarSearchSchema>

/** The task detail page: which activity filter is shown. */
export const taskDetailSearchSchema = z.object({
  activity: z.enum(['all', 'comments', 'history']).optional().catch(undefined),
})
export type TaskDetailSearch = z.infer<typeof taskDetailSearchSchema>

/** My work: the task open in the side panel. */
export const myWorkSearchSchema = z.object({
  task: uuid,
  new: flag,
})
export type MyWorkSearch = z.infer<typeof myWorkSearchSchema>
